// Repair API client — POST /repair/item.
//
// Session 19AU: client-side counterpart to packages/ingest/src/repair/.
// Talks to the same worker as ingest. Failures (non-2xx) become typed errors
// the caller (RepairDialog) can pattern-match on.
//
// 19AW Phase 2: extended response shape to surface guardReport / failureKind
// / promptVersion (worker now computes these); extended request shape to
// carry previousAttempts (Layer 3 retry hint) and sourceRef.

import type { RepairAttemptReason, GuardReport, RepairFailureKind } from '@examforge/shared/schemas';
import { RepairResponseSchema } from '@examforge/shared/schemas';

const INGEST_URL = (import.meta.env['VITE_INGEST_URL'] as string | undefined) ?? 'http://localhost:8787';

export type RepairableKind =
  | 'mcq' | 'tf' | 'flashcard' | 'essay' | 'glossary' | 'mindmap'
  | 'lecture_note' | 'proof_drill' | 'free_response' | 'reading_passage';

/** Layer-3 retry diagnostic — mirrors the type in packages/ingest/src/repair/
 *  retry-hints.ts (kept in client to avoid a worker-side import in the web
 *  bundle). Build via `toRetryDiagnostic(prevAttempt)` from repair-store. */
export interface RetryDiagnostic {
  timestamp: string;
  failureKind: RepairFailureKind;
  diagnostic: string;
}

export interface RepairRequest {
  kind: RepairableKind;
  itemJson: unknown;
  reason: RepairAttemptReason;
  contextItems?: unknown[];
  sourceSlice?: string;
  /** Forensic-only: chunk id this slice came from. */
  sourceRef?: string;
  /** 19AW Layer-3: prior failed attempts' diagnostic hints. Empty/undefined
   *  on the first attempt keeps the prompt cache-friendly. */
  previousAttempts?: RetryDiagnostic[];
}

// Session 39: RepairResponse type now derives from the shared Zod schema
// (packages/shared/src/schemas/repair-response.ts). Single source of truth.
export type RepairResponse =
  | {
      status: 'success';
      output: unknown;
      providerName: string;
      modelKey: string;
      inputTokens: number;
      outputTokens: number;
      costRmb: number;
      durationMs: number;
      /** 19AV Phase B: server-side mutability enforcement record. */
      guardReport: GuardReport;
      /** 19AW: 8-char sha256 prefix of the system prompt used by worker. */
      promptVersion: string;
      /** 19AW: present iff the guard reverted any locked field. */
      failureKind?: RepairFailureKind;
    }
  | {
      status: 'schema_fail';
      schemaErrors: string[];
      rawOutput: unknown;
      providerName: string;
      modelKey: string;
      inputTokens: number;
      outputTokens: number;
      costRmb: number;
      durationMs: number;
      failureKind: 'schema_violation';
      promptVersion: string;
    }
  | {
      status: 'provider_error';
      message: string;
      durationMs: number;
    };

export class RepairApiError extends Error {
  override readonly name = 'RepairApiError';
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Call POST /repair/item. Throws RepairApiError on HTTP failure. */
export async function postRepair(req: RepairRequest, anonId?: string): Promise<RepairResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (anonId) headers['X-Anon-Id'] = anonId;
  // Build body with only defined fields so passthrough zod parsing on worker
  // doesn't see explicit undefineds (which serialize to `null` in some libs).
  const body: Record<string, unknown> = {
    kind: req.kind,
    itemJson: req.itemJson,
    reason: req.reason,
  };
  if (req.contextItems !== undefined) body['contextItems'] = req.contextItems;
  if (req.sourceSlice !== undefined) body['sourceSlice'] = req.sourceSlice;
  if (req.sourceRef !== undefined) body['sourceRef'] = req.sourceRef;
  if (req.previousAttempts !== undefined) body['previousAttempts'] = req.previousAttempts;
  const res = await fetch(`${INGEST_URL}/repair/item`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  // Worker uses 200 for any reachable LLM outcome (success or schema_fail);
  // 400 = bad request, 429 = rate limit, 502 = provider error, 503 = no LLM
  // configured / fixture mode / budget exhausted.
  const json: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const j = json as { error?: string; message?: string };
    throw new RepairApiError(res.status, j.error ?? 'http_error', j.message ?? `HTTP ${res.status}`);
  }
  // Session 39: runtime Zod validation replaces bare `as` cast.
  // After safeParse confirms the shape, cast to the hand-written type
  // (Zod inference produces subtly different optional/required annotations).
  const parsed = RepairResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new RepairApiError(502, 'contract_violation',
      `Repair response schema validation failed: ${parsed.error.issues.map(i => i.message).join('; ')}`);
  }
  return parsed.data as RepairResponse;
}

/**
 * Map a thrown value from a repair call to a user-facing message. Extracted
 * (19BL Session 3b) from repair-dialog.tsx's catch block — where it lived
 * inline with zero test coverage. Pure + total: never throws (the caller is a
 * catch block; throwing here would swallow the original error).
 *
 * Branches mirror the worker's documented error codes (repair.ts POST /repair):
 *   fixture_mode   → real LLM not enabled on this deploy
 *   budget_exceeded→ backend spend cap fired
 *   rate_limited   → too many requests
 *   any other code → generic 修复失败 + the raw message
 *   non-RepairApiError → surface the message (or a safe fallback)
 */
export function mapRepairErrorToMessage(err: unknown): string {
  if (err instanceof RepairApiError) {
    switch (err.code) {
      case 'fixture_mode':
        return '当前部署未启用真实 LLM，无法修复（联系 operator）';
      case 'budget_exceeded':
        return '后端预算上限触发，请稍后重试';
      case 'rate_limited':
        return '请求太频繁，请稍候';
      default:
        return `修复失败：${err.message}`;
    }
  }
  if (err instanceof Error) {
    return err.message ?? '未知错误';
  }
  return '未知错误';
}
